import React, { useState } from 'react';
import Image from 'src/components/Image';
import { DemoTemplate } from 'src/templates';
import ContentCards from 'src/components/content-cards';
import { Button } from 'src/components/Inputs';
import { fetchMetrics, getLinkTokenEndpoint } from 'src/lib/utils';

import {
  metaData,
  pageHeaderMmdInitiationData,
  cardSectionData,
} from 'src/store/accounts';

const makeLinkTokenEndpoint = ({
  metrics,
  countryCode,
  language,
  accessToken,
}) => {
  const linkTokenEndpoint = new URL(getLinkTokenEndpoint());
  linkTokenEndpoint.searchParams.append('metrics_id', metrics.id);
  linkTokenEndpoint.searchParams.append('country_codes', countryCode);
  linkTokenEndpoint.searchParams.append('language', language);
  linkTokenEndpoint.searchParams.append('access_token', accessToken);
  return linkTokenEndpoint;
};

const fetchLinkToken = async ({ countryCode, language, accessToken }) => {
  const metrics = fetchMetrics();
  const linkTokenEndpoint = makeLinkTokenEndpoint({
    metrics,
    countryCode,
    language,
    accessToken,
  });
  try {
    const linkTokenResp = await fetch(linkTokenEndpoint.href);
    const linkToken = await linkTokenResp.json();
    return linkToken;
  } catch (e) {
    throw new Error(`Error fetching link token: ${e}`);
  }
};

const Accounts = () => {
  const [isLoading, setIsLoading] = useState(false);
  const handleClick = async (event) => {
    const verifyButton =
      event.target.ownerDocument.getElementById('verifyButton');
    const countryCode = verifyButton.getAttribute('countryCode');
    const language = verifyButton.getAttribute('language');
    const accessToken = verifyButton.getAttribute('token');

    setIsLoading(true);
    const linkTokenResp = await fetchLinkToken({
      countryCode,
      language,
      accessToken,
    });
    setIsLoading(false);
    if (!linkTokenResp) {
      // escape hatch for link token fetch failure
      return;
    }
    const token = linkTokenResp.linkToken;
    let linkHandler;

    // create a Link handler with the provided inputs
    const configs = {
      token: token,
      onSuccess: function (token) {
        window.location = `/demo/microdeposits/?public_token=${token}&product=auth`;
      },
      onExit: async function (err) {
        if (err != null && err.error_code === 'INVALID_LINK_TOKEN') {
          // if the item_add_token becomes invalid, recreate the link
          // handler with a new item_add_token
          linkHandler?.destroy();
          linkHandler = window?.Plaid?.create(
            {
              ...configs,
              token: await fetchLinkToken({
                countryCode,
                language,
                accessToken,
              }),
            },
            {
              enableFlexLink: true,
            },
          );
        }
      },
    };

    // create a Link handler with the provided inputs
    linkHandler = window?.Plaid?.create(configs, {
      enableFlexLink: true,
    });

    // open Link
    linkHandler?.open();
  };

  return (
    <DemoTemplate {...metaData}>
      <div className='page-header page-header-web3-theme background background--color-black100'>
        <div className='grid-container'>
          <div className='grid-x align-center grid-margin-x'>
            <div className='cell small-12 medium-4'>
              <div className='grid-x'>
                <div className='cell small-12 page-header-breakpoint-12'>
                  <h1 className='page-header-header'>
                    {pageHeaderMmdInitiationData.status}
                  </h1>
                  <h4 className='regular page-header-subheader'>
                    {pageHeaderMmdInitiationData.subtext}
                  </h4>
                  <Button
                    id='verifyButton'
                    className='cell medium-12 small-12 button'
                    onClick={handleClick}
                    fullWidth
                    {...(isLoading ? { disabled: true } : {})}
                    style={{
                      visibility: 'hidden',
                      width: '80%',
                      marginBottom: '16px',
                    }}
                  >
                    Verify Account
                  </Button>
                </div>
              </div>
            </div>
            <div className='cell small-12 medium-8 page-header-bg-wrapper'>
              <div>
                <div className='demo-accent'>
                  <Image
                    src='https://images.ctfassets.net/ss5kfr270og3/3rdsesKs5mC5bNKBlUWfAk/cd07dad6bcee7d1f4e13ecebda0e4816/homepage-howitworks-brushstroke-left.png?fm=webp&q=70'
                    alt=''
                  />
                </div>
                <div className='code-container code-container--tall code-container--z-index code-container--no-margin'>
                  <header className='code-container-header'>
                    <p className='code-container-header-title'>
                      POST /accounts/get response
                    </p>
                    <div
                      data-toggle-code-example=''
                      className='button-code-toggle-wrapper'
                    >
                      <button className='button button-code-toggle is-active clear'>
                        RENDERED
                      </button>{' '}
                      <button className='button button-code-toggle clear'>
                        JSON
                      </button>
                    </div>
                  </header>
                  <div className='code-container-body code-product-example'>
                    <div className='rendered pdf is-active'>
                      <div className='explainer'>
                        <p className='balance-header'>Account data returned:</p>
                        <div id='account-items'></div>
                      </div>
                    </div>
                    <div className='code-example'>
                      <pre>
                        {' '}
                        <code
                          id='accounts-get-response'
                          className='language-json'
                        >{`
                    `}</code>
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ContentCards {...cardSectionData} />
    </DemoTemplate>
  );
};

export default Accounts;
